import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnInit,
  Renderer2,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ZonePageUrlProvider } from '@app/finxone-web-frontend/app/Modules/zones/zone-url-provider';
import { DedupeInterceptor } from '@app/finxone-web-frontend/app/lib/dedup-interceptor';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { CtaButtonSignalService } from '@app/finxone-web-frontend/app/lib/services/cta-button-signal-service/cta-button-signal.service';
import { GoogleAnalyticsService } from '@app/finxone-web-frontend/app/lib/services/google-analytics/google-analytic.service';
import { WorkflowFormsService } from '@app/finxone-web-frontend/app/lib/services/workflow-forms/workflow-forms.service';
import {
  FormActionState,
  FormActionStateModel,
} from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import {
  ctaButtonFunctionsMap,
  functionCtaType,
} from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-button-actions.utils';
import { ctaButtonValidationMap } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-button-validations/cta-button-validation.utils';
import { ctaButtonShowHideMap } from '@app/finxone-web-frontend/app/lib/utils/cta-button-actions/cta-button-visibility-validations/cta-button-visibility.utils';
import { buildHandleBarModel } from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  ButtonLoadingIndicatorType,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  commonContentStyleList,
  commonTextContentStyleList,
  resolveButtonLoadingConfigFromCss,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, filter, map, takeUntil } from 'rxjs';
import { BaseComponent } from '../../base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-cta-button-widget',
  templateUrl: './cta-button-widget.component.html',
  styleUrls: ['./cta-button-widget.component.scss'],
})
export class CtaButtonWidgetComponent extends BaseComponent implements OnInit, AfterViewInit {
  @ViewChild('buttonContent', { static: false })
  public buttonContent: ElementRef;
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public buttonLabel: string;
  public buttonLoadingText = '';
  public buttonIcon: string;
  public updatedBeneficiaryName: string;
  @Select(FormActionState.getFormActionState)
  formActionState!: Observable<FormActionStateModel>;
  public lefticon: string;
  public righticon: string;
  public leftIconStyles?: {
    width: string;
    height: string;
    color: string | undefined;
  };
  public rightIconStyles?: {
    width: string;
    height: string;
    color: string | undefined;
  };
  onboardingOrgId: string;
  isButtonActive = true;
  pageUrl = '';
  formActionDataWithId: any;
  public marginStyle: baseCssConfigStyle;
  isButtonHideShow = false;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;
  public isLoadingState = false;
  public activationDelaySeconds?: number;
  public globalLoadingIndicatorType = ButtonLoadingIndicatorType.ANIMATION;
  public globalLoadingText = '';
  private isExecuting = false;
  private static readonly LOADING_SHOW_DELAY_MS = 500;
  private static readonly LOADING_MAX_TIMEOUT_MS = 30_000;

  private renderer = inject(Renderer2);
  private cdr = inject(ChangeDetectorRef);
  public store = inject(Store);
  private injector = inject(Injector);
  private configService = inject(ConfigService);
  public dataIn = inject(ProviderClass<UiZoneWidgetAttributeConfig>);
  public zoneUrlprovider = inject(ZonePageUrlProvider);
  private countriesCurrenciesService = inject(CountriesCurrenciesService);
  private ctaButtonSignalService = inject(CtaButtonSignalService);
  private iconCacheService = inject(IconCacheService);
  private googleAnalyticService = inject(GoogleAnalyticsService);
  private workflowFormsService = inject(WorkflowFormsService);
  private router = inject(Router);
  private dedupeInterceptor = inject(DedupeInterceptor);

  constructor() {
    super();
    this.formActionState
      .pipe(
        takeUntil(this.destroy$),
        map((response) => {
          this.formActionDataWithId = response;
        }),
      )
      .subscribe();
    this.pageUrl = this.zoneUrlprovider.pageUrlInput;
    const buttonType = this.dataIn.attri?.widgetProperties?.['buttonActionType'] ?? '';
    effect(
      () => (
        (this.isButtonActive =
          this.ctaButtonSignalService.buttonDisabled().find((button) => buttonType in button)?.[buttonType] ??
          true),
        (this.isButtonHideShow =
          this.ctaButtonSignalService.buttonHide().find((button) => buttonType in button)?.[buttonType] ??
          false)
      ),
    );
  }

  ngOnInit() {
    this.onLoadComponent();
    this.checkButtonStatus();
    this.checkButtonShowHideStatus();
  }

  ngAfterViewInit(): void {
    this.onSetDynamicStyle();
  }

  private onLoadComponent() {
    if (this.dataIn != undefined) {
      this.attri = this.dataIn.attri;
      this.getGlobalStyling(); // must run first: globalStylingClass/isAllowedGlobalStyling feed the global loading lookup below
      const labelOfButton = this.attri.widgetProperties['textContent'];
      if (this.attri.widgetProperties?.['navigationConfig']) {
        alert(
          'This action button contains deprecated property "navigationConfig". Please update the configuration to use "actionNavigationConfigurations" instead. Please review and update the configuration accordingly.',
        );
      }
      const template = Handlebars.compile(labelOfButton);
      const loadingText = this.attri.widgetProperties?.['loadingTextContent'] ?? '';
      const loadingTextTemplate = Handlebars.compile(loadingText);

      let combinedTemplates = `${labelOfButton ?? ''} ${loadingText}`;
      let globalLoadingTemplate: Handlebars.TemplateDelegate | undefined;

      const { indicatorType, rawLoadingText } = resolveButtonLoadingConfigFromCss(
        this.configService.globalCssVariables(),
        this.globalStylingClass,
      );
      this.globalLoadingIndicatorType = indicatorType;
      if (rawLoadingText) {
        globalLoadingTemplate = Handlebars.compile(rawLoadingText);
        combinedTemplates += ` ${rawLoadingText}`;
      }

      buildHandleBarModel(
        this.store,
        combinedTemplates,
        this.countriesCurrenciesService,
        this.iconCacheService,
      )
        .pipe(takeUntil(this.destroy$))
        .subscribe((model) => {
          this.buttonLabel = template(model);
          this.buttonLoadingText = loadingTextTemplate(model);
          if (globalLoadingTemplate) {
            this.globalLoadingText = globalLoadingTemplate(model);
          }
        });
      this.buttonIcon = this.attri.widgetProperties?.icon as string;
      this.leftIconStyles = {
        width:
          (this.attri.css_config['lefticonSize'] ?? 20) + (this.attri.css_config['leftIconUnit'] ?? 'px'),
        height:
          (this.attri.css_config['lefticonSize'] ?? 20) + (this.attri.css_config['leftIconUnit'] ?? 'px'),
        color: this.attri.css_config['leftIconColor'],
      };
      this.rightIconStyles = {
        width:
          (this.attri.css_config['righticonSize'] ?? 20) + (this.attri.css_config['rightIconUnit'] ?? 'px'),
        height:
          (this.attri.css_config['righticonSize'] ?? 20) + (this.attri.css_config['rightIconUnit'] ?? 'px'),
        color: this.attri.css_config['iconColor'] ?? this.attri.css_config['rightIconColor'],
      };

      this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
        ...commonContentStyleList,
        ...commonTextContentStyleList,
      ]);

      if (this.attri.widgetProperties?.lefticon || this.attri.widgetProperties?.righticon) {
        this.buttonIcon = '';
        this.lefticon = this.attri.widgetProperties?.lefticon as string;
        this.righticon = this.attri.widgetProperties?.righticon as string;
      }
      this.activationDelaySeconds = this.attri.widgetProperties?.delayButtonActivation
        ? this.attri.widgetProperties?.activationDelaySeconds
        : undefined;
    }
  }

  private getGlobalStyling() {
    if (
      this.dataIn.attri.widgetProperties?.['globalStylingClass'] &&
      this.dataIn.attri.widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass = this.dataIn.attri.widgetProperties?.['globalStylingClass'] ?? '';
      this.isAllowedGlobalStyling = this.dataIn.attri.widgetProperties?.['globalStyling'] ?? false;
    }
  }

  public doAction(): void {
    if (this.isExecuting) {
      return; // click guard: ignore repeat clicks while an action is in flight
    }
    this.googleAnalyticService.sendEvent(
      this.attri.widgetProperties?.['buttonActionType'] ?? 'action-button',
    );
    const ctaActionType = this.attri.widgetProperties?.['buttonActionType'];
    if (!ctaActionType) {
      return;
    }
    const buttonAction = ctaButtonFunctionsMap.get(ctaActionType); // get the action by passing the button action type
    if (!buttonAction) {
      console.error('Button action not defined, please check');
      return;
    }
    this.executeWithLoadingState(buttonAction);
  }

  /**
   * Runs a cta-button-actions function while tracking its completion generically: shows the
   * configured loading state after LOADING_SHOW_DELAY_MS if the click hasn't settled yet, and
   * clears it once every HTTP request the click triggered (directly, or via a dispatched NGXS
   * effect chain) has finished, on navigation, or on a defensive max timeout - whichever first.
   * See docs/stories/9806-action-button-loading-states/4-request-activity-tracker-and-executor-implementation-plan.md
   */
  private executeWithLoadingState(buttonAction: functionCtaType): void {
    this.isExecuting = true;
    const showLoadingTimer = setTimeout(
      () => (this.isLoadingState = true),
      CtaButtonWidgetComponent.LOADING_SHOW_DELAY_MS,
    );

    const baseline = this.dedupeInterceptor.snapshotActiveIds();
    const correlated = new Set<string>();
    let settled = false;

    const finishExecuting = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(showLoadingTimer);
      clearTimeout(maxTimeoutHandle);
      activitySub.unsubscribe();
      navSub.unsubscribe();
      this.isExecuting = false;
      this.isLoadingState = false;
      this.cdr.markForCheck();
    };

    // fold newly-seen ids into `correlated`; finish once it drains back to empty
    const activitySub = this.dedupeInterceptor.activityEvents$
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ id, phase }) => {
        if (phase === 'start' && !baseline.has(id)) {
          correlated.add(id);
        }
        if (phase === 'end') {
          correlated.delete(id);
          if (correlated.size === 0) {
            finishExecuting();
          }
        }
      });

    // clear on navigation regardless of in-flight requests (preserves today's behaviour)
    const navSub = this.router.events
      .pipe(
        filter((routerEvent): routerEvent is NavigationEnd => routerEvent instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => finishExecuting());

    const maxTimeoutHandle = setTimeout(finishExecuting, CtaButtonWidgetComponent.LOADING_MAX_TIMEOUT_MS);

    const actionResult = buttonAction(this.injector, this.attri);
    if (actionResult instanceof Promise) {
      actionResult
        .catch((error: unknown) => console.error('Button action failed', error))
        .finally(() => {
          // Macrotask, not a microtask check, for the same reason as the sync-path check below:
          // a function whose last statement is a fire-and-forget `.subscribe()` (never awaited)
          // resolves its own Promise before Angular's HttpClient/interceptor chain gets to run -
          // that chain takes its own extra microtask hop(s) to reach DedupeInterceptor, so
          // checking `correlated` synchronously here can wrongly see "no HTTP traffic" and tear
          // everything down moments before the request's 'start' event actually arrives.
          setTimeout(() => {
            if (correlated.size === 0) {
              finishExecuting();
            }
          }, 0);
        });
    } else {
      // Synchronous `void` return: check on a macrotask, not a microtask. NGXS's dispatch()
      // synchronously invokes the @Action handler and the service method that builds the
      // HttpClient Observable, but the actual subscribe() that hands the request to
      // DedupeInterceptor happens through NGXS's own internal Actions/forkJoin plumbing one (or
      // more) microtask ticks later - a queueMicrotask() callback here would run BEFORE that and
      // wrongly conclude "no HTTP traffic", tearing down activitySub/showLoadingTimer before the
      // real request ever arrives. A macrotask (setTimeout 0) always runs after the entire
      // microtask queue has drained, however many hops NGXS takes, so it's the reliable check.
      setTimeout(() => {
        if (correlated.size === 0) {
          finishExecuting();
        }
      }, 0);
    }
  }

  public onSetDynamicStyle(): void {
    if (this.dataIn?.attri && this.buttonContent?.nativeElement) {
      this.renderer.setStyle(
        this.buttonContent.nativeElement,
        '--form-submission-button-disabled-background-color',
        this.dataIn.attri.css_config?.disabledBgColor,
        2,
      );
      this.renderer.setStyle(
        this.buttonContent.nativeElement,
        '--form-submission-button-disabled-text-color',
        this.dataIn.attri.css_config?.disabledTextColor,
        2,
      );

      this.renderer.setStyle(
        this.buttonContent.nativeElement,
        '--form-submission-button-text-color',
        this.dataIn.attri.css_config?.color,
        2,
      );

      this.cdr.detectChanges(); // Manually trigger change detection
    }
  }

  public checkButtonShowHideStatus(): void {
    const ctaActionType = this.attri.widgetProperties?.['buttonActionType'];
    if (ctaActionType) {
      const buttonAction = ctaButtonShowHideMap.get(ctaActionType);
      if (buttonAction) {
        buttonAction(
          this.store,
          this.ctaButtonSignalService,
          this.attri.widgetProperties as any,
          this.configService,
        );
      }
    }
  }
  public checkButtonStatus(): void {
    const ctaActionType = this.attri.widgetProperties?.['buttonActionType'];
    if (ctaActionType) {
      const buttonAction = ctaButtonValidationMap.get(ctaActionType);
      if (buttonAction) {
        buttonAction(
          this.store,
          this.ctaButtonSignalService,
          this.attri.widgetProperties as any,
          this.configService,
          this.workflowFormsService,
        );
      }
    }
  }
}
